import virtualTerminalStore from "../../../store/VirtualTerminalStore";
import {
  getMode,
  googlePayAdapter,
  isWalletPaymentSuccess,
  postExpressCheckoutEvent,
  processWalletPayment,
  walletErrorFromResult,
} from "..";
import { runWithToken } from "../token-queue";

export class GooglePay {
  urlApi;
  paypoint;
  payabliConfig;
  googlePayRequestPayload;
  paymentRequest;
  domain;
  isProcessing = false;
  buttonCreated = false;

  static SUPPORTED_BUTTON_TYPES = [
    "book",
    "buy",
    "checkout",
    "donate",
    "order",
    "pay",
    "plain",
    "long",
    "short",
    "subscribe",
  ];

  constructor({ config, paypoint, domain }) {
    const { expressCheckout = {} } = config;
    this.urlApi = process.env.REACT_APP_URL_API;
    this.payabliConfig = config;
    this.domain = domain;
    this.paypoint = paypoint;
    this.mode = getMode(config);

    this.baseRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
    };

    const tokenizationSpecification = {
      type: "PAYMENT_GATEWAY",
      parameters: {
        gateway: "payabli",
        gatewayMerchantId: this.payabliConfig.entryPoint,
      },
    };

    const allowedCardNetworks =
      (expressCheckout.supportedNetworks || []).map((v) => v.toUpperCase()) ||
      [];

    const allowedCardAuthMethods = ["PAN_ONLY", "CRYPTOGRAM_3DS"];

    this.baseCardPaymentMethod = {
      type: "CARD",
      parameters: {
        allowedAuthMethods: allowedCardAuthMethods,
        allowedCardNetworks: allowedCardNetworks,
        billingAddressRequired: true,
        billingAddressParameters: {
          format: "FULL",
        },
      },
    };

    this.cardPaymentMethod = {
      ...this.baseCardPaymentMethod,
      tokenizationSpecification,
    };

    this.isReadyToPayRequest = {
      ...this.baseRequest,
      allowedPaymentMethods: [this.baseCardPaymentMethod],
    };
  }

  encodeBase64(str) {
    try {
      return btoa(unescape(encodeURIComponent(str)));
    } catch (error) {
      this.sendError(error, "Error encoding base64");
      return null;
    }
  }

  sendError(error, context = "Google Pay Error") {
    console.error(`${context}:`, error);
    postExpressCheckoutEvent({
      kind: "error",
      randomId: this.payabliConfig.randomId,
      data: { error, paymentMethod: googlePayAdapter.walletType },
    });
  }

  async onPaymentAuthorized(paymentData) {
    let flow = "one_time";
    try {
      const result = await runWithToken(async () => {
        const paid = await processWalletPayment({
          adapter: googlePayAdapter,
          config: this.payabliConfig,
          paypoint: this.paypoint,
          urlApi: this.urlApi,
          token: virtualTerminalStore.token,
          event: paymentData,
        });
        // Temporary tokens are single-use: keep the rotated token the backend
        // returns on every response, errors included, so a retry is authorized.
        // A response without a rotated token means the stored one is spent:
        // clear it so a later call fails closed.
        virtualTerminalStore.setToken(paid.data?.pageIdentifier || "");
        return paid;
      });
      flow = result.flow;
      const { ok, data, walletType } = result;

      // Don't rely on `ok` alone or a generic message: the API returns its
      // error envelope (responseCode + responseData.explanation) even on a 4xx
      // status, so surface the real reason it sent back.
      if (!isWalletPaymentSuccess({ ok, data })) {
        throw walletErrorFromResult({ data });
      }

      postExpressCheckoutEvent({
        kind: "success",
        randomId: this.payabliConfig.randomId,
        data: { data, paymentMethod: walletType, flow },
      });
      return { transactionState: "SUCCESS" };
    } catch (error) {
      postExpressCheckoutEvent({
        kind: "error",
        randomId: this.payabliConfig.randomId,
        data: {
          error,
          paymentMethod: googlePayAdapter.walletType,
          flow,
        },
      });
      return {
        transactionState: "ERROR",
        error: {
          reason: "PAYMENT_DATA_INVALID",
          message: error.message,
          intent: "PAYMENT_AUTHORIZATION",
        },
      };
    }
  }

  async googlePayInit() {
    try {
      if (!window.google || !window.google.payments) {
        throw new Error("Google Pay SDK not loaded");
      }

      const isProduction =
        process.env.REACT_APP_GOOGLE_PAY_ENVIRONMENT?.toLowerCase() ===
        "production";
      const environment = isProduction ? "PRODUCTION" : "TEST";

      // eslint-disable-next-line no-undef
      const paymentsClient = new google.payments.api.PaymentsClient({
        environment: environment,
        paymentDataCallbacks: {
          onPaymentAuthorized: (paymentData) =>
            this.onPaymentAuthorized(paymentData),
        },
      });

      this.paymentsClient = paymentsClient;

      if (!this.isReadyToPayRequest) {
        throw new Error("isReadyToPayRequest is missing");
      }

      const response = await paymentsClient.isReadyToPay(
        this.isReadyToPayRequest
      );
      if (!response.result) {
        throw new Error("Google Pay is not available for this user");
      }

      return { ok: true };
    } catch (error) {
      console.error("Google Pay initialization failed:", error);
      this.sendError(error, "Google Pay initialization failed");
      throw error;
    }
  }

  createButton() {
    // Prevent multiple button creation
    if (this.buttonCreated) {
      console.warn("Google Pay button already created");
      return;
    }

    const { expressCheckout = {} } = this.payabliConfig;
    const { googlePay = {}, appearance = {} } = expressCheckout;
    const paymentDataRequest = this.getPaymentDataRequest({
      fee: expressCheckout.fee,
      amount: expressCheckout.amount,
      currency: expressCheckout.currency,
      entryPoint: this.payabliConfig.entryPoint,
      dbaName: this.paypoint?.DbaName,
    });

    const defaultButtonType = (() => {
      const mode = getMode(this.payabliConfig);
      if (mode === 'autopay') return 'subscribe';
      if (mode === 'tokenization') return 'plain';
      return 'pay';
    })();
    const buttonType = GooglePay.SUPPORTED_BUTTON_TYPES.includes(
      googlePay?.buttonType
    )
      ? googlePay?.buttonType
      : defaultButtonType;

    const button = this.paymentsClient.createButton({
      onClick: async () => {
        // Prevent multiple simultaneous calls
        if (this.isProcessing) {
          console.warn("Google Pay transaction already in progress");
          return;
        }

        this.isProcessing = true;
        try {
          await this.paymentsClient.loadPaymentData(paymentDataRequest);
        } catch (error) {
          console.error("Google Pay Error:", error);

          const eventType =
            !error || error.statusCode === "CANCELED"
              ? `callback-payabli-function-expresscheckout-cancel${this.payabliConfig.randomId}`
              : `callback-payabli-function-expresscheckout-error${this.payabliConfig.randomId}`;

          window.parent.postMessage(
            {
              event: eventType,
              data: { error, paymentMethod: "google_pay", flow: getMode(this.payabliConfig) },
            },
            "*"
          );
        } finally {
          this.isProcessing = false;
        }
      },
      allowedPaymentMethods: [],
      buttonLocale: googlePay?.language || "en",
      buttonColor: googlePay?.buttonStyle || "black",
      buttonType,
      buttonSizeMode: "fill",
      buttonRadius: appearance?.buttonBorderRadius || 4,
    });

    const buttonContainer = document.getElementById("google-pay-button");
    if (!buttonContainer) {
      throw new Error("Google Pay button container not found");
    }

    // Clear any existing buttons before appending
    buttonContainer.innerHTML = "";
    buttonContainer.appendChild(button);
    this.buttonCreated = true;
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      if (document.getElementById("google-pay-script")) {
        if (window.google && window.google.payments) {
          console.info("Google Pay SDK already loaded");
          resolve({ ok: true });
        } else {
          console.warn(
            "Google Pay script found but SDK not ready. Retrying..."
          );
          setTimeout(() => this.loadScript().then(resolve).catch(reject), 500);
        }
        return;
      }

      const script = document.createElement("script");
      script.id = "google-pay-script";
      script.src = "https://pay.google.com/gp/p/js/pay.js";
      script.async = true;
      document.head.appendChild(script);

      script.onload = () => {
        if (!window.google || !window.google.payments) {
          console.error("Google Pay SDK failed to initialize properly");
          reject(new Error("Google Pay SDK not available after script load"));
          return;
        }

        console.info("Google Pay SDK loaded successfully");

        this.googlePayInit()
          .then(resolve)
          .catch((error) => {
            console.error("Google Pay initialization failed:", error);
            reject(error);
          });
      };

      script.onerror = (error) => {
        console.error("Failed to load Google Pay script:", error);
        reject(error);
      };
    });
  }

  getPaymentDataRequest({
    fee = 0,
    amount = 0,
    currency = "USD",
    entryPoint,
    dbaName,
  }) {
    const totalAmount = (Number(amount) + Number(fee)).toFixed(2);
    return {
      ...this.baseRequest,
      allowedPaymentMethods: [this.cardPaymentMethod],
      transactionInfo: {
        totalPriceStatus: "FINAL",
        totalPriceLabel: "Total",
        displayItems: [
          {
            label: this.mode === 'tokenization' ? "Authorization" : "Subtotal",
            type: "SUBTOTAL",
            price: this.mode === 'tokenization' ? "0.01" : Number(amount).toFixed(2),
          },
          ...(this.mode !== 'tokenization'
            ? [
                {
                  label: "Service Fee",
                  type: "LINE_ITEM",
                  price: Number(fee).toFixed(2),
                },
              ]
            : []),
        ],
        totalPrice: this.mode === 'tokenization' ? "0.01" : totalAmount,
        currencyCode: currency,
        countryCode: "US",
      },
      merchantInfo: {
        merchantName: dbaName || "Unknown Merchant",
        merchantId: process.env.REACT_APP_GOOGLE_MERCHANT_ID,
        merchantOrigin: this.domain,
      },
      callbackIntents: ["PAYMENT_AUTHORIZATION"],
    };
  }
}
